import 'classlist-polyfill'
import 'promise-polyfill/src/polyfill'

import React, { FC, useState, useEffect, useCallback } from 'react'
import Chart from "react-apexcharts"

import {
  nowOptions as initialNowOptions,
  pastOptions as initialPastOptions,
} from './VariablesPieChart'

import Loading from '../common/Loading'

import { MemberReportApplicationProportion } from '../../../states/types/member'

import { format, combineYearMonthAndDay, diffDays, dateManipulateDay, prevMonth } from '../../../utils/date'

import { useTranslation } from 'react-i18next'
import TransAppName from './TransAppName'
import { useHistory, useParams } from 'react-router-dom'
import { LangValue } from '../../../states/types/common'
import { map, uniqueId } from 'lodash'
import { convertHexColor } from '../../../utils/color'
import InfoTips from '../common/InfoTips'

interface ApplicationProportionProps {
  isLoading: boolean | undefined
  applicationProportion: MemberReportApplicationProportion | undefined
  date: string | {start_date: string, end_date: string}
  mode: "daily" | "monthly" | "custom"
  setApplicationFilter: (applicationFilter: string) => void
}

const ApplicationProportion: FC<ApplicationProportionProps> = (props) => {
  const { t } = useTranslation()
  const { language } = useParams<{ language: LangValue }>()
  const history = useHistory()

  const {
    isLoading,
    applicationProportion,
    date,
    mode,
    setApplicationFilter,
  } = props

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [nowChartData, setNowChartData] = useState<any>(undefined)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [pastChartData, setPastChartData] = useState<any>(undefined)

  const nowRef = useCallback((node: HTMLInputElement | null) => {
    if (!node) return
    $(node).on('click', ".apexcharts-pie-series", (event) => {
      if (!nowChartData) return
      setApplicationFilter(nowChartData.options.labels[Number(event.target.getAttribute("j"))])
    })
  }, [nowChartData])

  const pastRef = useCallback((node: HTMLInputElement | null) => {
    if (!node || mode !== "monthly" || typeof date !== "string") return
    $(node).on('click', ".apexcharts-pie-series", (event) => {
      if (!pastChartData) return
      const filterWord = pastChartData.options.labels[Number(event.target.getAttribute("j"))]
      history.push(`?type=monthly&target_month=${prevMonth(combineYearMonthAndDay(date, 1), "YYYYMM")}&application_filter=${filterWord}`)
    })
  }, [pastChartData])

  const translateAppName = (appName: string) => {
    let app = appName
    if (app === 'other') {
      app = t('parts.app_name.other')
    }
    return app
  }

  useEffect(() => {
    if (!applicationProportion) return
    const nowChartOptions = initialNowOptions
    nowChartOptions.labels = map(applicationProportion.now.app, (app) => translateAppName(app))
    nowChartOptions.colors = applicationProportion.now.color.map((value) => convertHexColor(value, 0.8))

    const pastChartOptions = initialPastOptions
    pastChartOptions.labels = map(applicationProportion.past.app, (app) => translateAppName(app))
    pastChartOptions.colors = applicationProportion.past.color.map((value) => convertHexColor(value, 0.8))
    pastChartOptions.plotOptions.pie.expandOnClick = false
    pastChartOptions.states.hover.filter.type = "none"
    pastChartOptions.states.active.filter.type = "none"
    setNowChartData({
      options: nowChartOptions,
      series: applicationProportion.now.value,
    })
    setPastChartData({
      options: pastChartOptions,
      series: applicationProportion.past.value,
    })
  }, [applicationProportion])

  const presentTitle = (type: string) => {
    if (type === "daily") {
      return (
        `${format(date, t('time.date.formats.with_weekday_no_year'), language)}`
      )
    }
    if (type === "monthly" && typeof date === 'string') {
      return (
        `${combineYearMonthAndDay(date, 1, t('time.date.formats.only_month'), language)}`
      )
    }
    if (type === "custom" && typeof date !== 'string') {
      return (
        // eslint-disable-next-line max-len
        `${format(date.start_date, t('time.date.formats.with_weekday_no_year'), language)} ~ ${format(date.end_date, t('time.date.formats.with_weekday_no_year'), language)}`
      )
    }
    return ""
  }

  const pastTitle = (type: string) => {
    if (type === "daily") {
      return (
        `${t('parts.application_proportion.past_30_average')}`
      )
    }
    if (type === "monthly" && typeof date === 'string') {
      return (
        `${format(prevMonth(combineYearMonthAndDay(date, 1)), t('time.date.formats.only_month'), language)}`
      )
    }
    if (type === "custom" && typeof date !== 'string') {
      const diffCount = (diffDays(date.start_date, date.end_date) * -1) - 1
      const pastStartDate = format(dateManipulateDay(date.start_date, diffCount), t('time.date.formats.with_weekday_no_year'), language)
      const pastEndDate = format(dateManipulateDay(date.end_date, diffCount), t('time.date.formats.with_weekday_no_year'), language)
      return (
        `${pastStartDate} ~ ${pastEndDate}`
      )
    }
    return ""
  }

  return (
    <div style={{ marginLeft: "0.5em", marginRight: "0.5em" }}>
      <div className="box">
        <div style={{ paddingTop: "1em", paddingBottom: "1em", textAlign: "center" }}>
          <span style={{ fontSize: "24px", color: "#094E8D", fontWeight: "bold" }}>{t('parts.application_proportion.title')}</span>
        </div>
        <InfoTips>
          <>
            <div>
              各アプリケーションの使用割合を表示しています
            </div>
          </>
        </InfoTips>
        <div className="flex">
          <div style={{ width: "50%", borderTop: "1px solid rgba(51, 51, 51, 0.2)", borderRight: "1px solid rgba(51, 51, 51, 0.2)" }}>
            <div style={{ paddingLeft: "0.75em", paddingTop: "0.5em", color: "rgba(51, 51, 51, 0.75)" }}>
              <span style={{ fontSize: "24px" }}>
                {presentTitle(mode)}
              </span>
            </div>
            {
              (isLoading || !nowChartData) ? (
                <Loading />
              ) : (
                <>
                  <div style={{ marginBottom: "1em" }} ref={nowRef}>
                    <Chart
                      options={nowChartData.options}
                      type="pie"
                      series={nowChartData.series}
                      height="300px"
                    />
                  </div>
                  <div
                    className="flex"
                    style={{
                      justifyContent: "center",
                      alignItems: "center",
                      flexWrap: "wrap",
                      paddingBottom: "1em",
                      width: "90%",
                      margin: "0 auto",
                    }}
                  >
                    {
                      applicationProportion?.now.app.map((item, index) => (
                        <div key={uniqueId()} className="flex" style={{ justifyContent: "center", alignItems: "center", paddingRight: "0.5em" }}>
                          <div style={{ width: "1em", height: "1em", background: applicationProportion?.now.color[index] }} />
                          <span style={{ paddingLeft: "0.25em", fontSize: "14px" }}><TransAppName appName={item} /></span>
                        </div>
                      ))
                    }
                  </div>
                </>
              )
            }
          </div>
          <div style={{ width: "50%", borderTop: "1px solid rgba(51, 51, 51, 0.2)" }}>
            <div style={{ paddingLeft: "0.75em", paddingTop: "0.5em", color: "rgba(51, 51, 51, 0.75)" }}>
              <span style={{ fontSize: "24px" }}>
                {pastTitle(mode)}
              </span>
            </div>
            {
              // eslint-disable-next-line no-nested-ternary
              (isLoading || !pastChartData) ? (
                <Loading />
              ) : (
                  applicationProportion?.past.app.length === 0 ? (
                    <div
                      style={{
                        marginBottom: "1em",
                        position: "absolute",
                        top: "50%",
                        left: "74%",
                        transform: "translateY(-50%) translateX(-50%)",
                      }}
                    >
                      <div style={{ textAlign: "center" }}>
                        <span style={{ fontSize: "14px" }}>---</span>
                      </div>
                      <div>
                        <span style={{ fontSize: "14px" }}>{t('parts.application_proportion.no_past_data')}</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ marginBottom: "1em" }} ref={pastRef}>
                        <Chart
                          options={pastChartData.options}
                          type="pie"
                          series={pastChartData.series}
                          height="300px"
                        />
                      </div>
                      <div
                        className="flex"
                        style={{
                          justifyContent: "center",
                          alignItems: "center",
                          flexWrap: "wrap",
                          paddingBottom: "1em",
                          width: "90%",
                          margin: "0 auto",
                        }}
                      >
                        {
                          applicationProportion?.past.app.map((item, index) => (
                            <div key={uniqueId()} className="flex" style={{ justifyContent: "center", alignItems: "center", paddingRight: "0.5em" }}>
                              <div style={{ width: "1em", height: "1em", background: applicationProportion?.past.color[index] }} />
                              <span style={{ paddingLeft: "0.25em", fontSize: "14px" }}><TransAppName appName={item} /></span>
                            </div>
                          ))
                        }
                      </div>
                    </>
                  )
              )
            }
          </div>
        </div>
      </div>
    </div>
  )
}

export default ApplicationProportion
